import React, { useState, useEffect } from 'react';
import { Amplify } from 'aws-amplify';
import { 
  withAuthenticator, 
  WithAuthenticatorProps,
  ThemeProvider
} from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import TransactionForm from './components/TransactionForm';
import TransactionList from './components/TransactionList';
import AuditLog from './components/AuditLog';
import Report from './components/Report';
import UserManagement from './components/UserManagement';
import suppressResizeObserverError from './utils/errorHandling';
import './App.css';
import { getCurrentUser } from 'aws-amplify/auth';
import { generateClient } from 'aws-amplify/api';
import { getUser, listUsers } from './graphql/queries';
import { createUser } from './graphql/mutations';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import awsconfig from './aws-exports';

// Import branding assets
import icon from './assets/stratto-icon.png';

// Configure Amplify
Amplify.configure(awsconfig);

type View = 'transactions' | 'audit' | 'report' | 'users';

// Custom authentication theme
const theme = {
  name: 'almida-theme',
  tokens: {
    colors: {
      brand: {
        primary: {
          10: { value: '#FFF1EF' },
          20: { value: '#FFE4E0' },
          40: { value: '#FFB3A7' },
          60: { value: '#FF8A7A' },
          80: { value: '#FF5E45' },
          90: { value: '#E54E35' },
          100: { value: '#CC3D24' }
        }
      },
      background: {
        primary: { value: '#FFFFFF' },
        secondary: { value: '#F5F5F5' }
      },
      font: {
        interactive: { value: '#FF5E45' }
      }
    },
    components: {
      button: {
        primary: {
          backgroundColor: { value: '#FF5E45' },
          color: { value: '#FFFFFF' },
          borderRadius: { value: '8px' },
          fontSize: { value: '16px' },
          fontWeight: { value: 600 },
          _hover: {
            backgroundColor: { value: '#E54E35' }
          },
          _active: {
            backgroundColor: { value: '#CC3D24' }
          },
          _focus: {
            boxShadow: { value: '0 0 0 2px #FFE4E0' }
          }
        }
      },
      fieldcontrol: {
        borderRadius: { value: '8px' },
        borderColor: { value: '#E0E0E0' },
        fontSize: { value: '16px' },
        _focus: {
          borderColor: { value: '#FF5E45' },
          boxShadow: { value: '0 0 0 2px #FFE4E0' }
        }
      },
      heading: {
        color: { value: '#333333' },
        fontSize: { value: '24px' },
        fontWeight: { value: '600' }
      },
      text: {
        color: { value: '#666666' },
        fontSize: { value: '14px' }
      }
    },
    shadows: {
      small: { value: '0 2px 4px rgba(0, 0, 0, 0.05)' },
      medium: { value: '0 4px 8px rgba(0, 0, 0, 0.1)' }
    },
    space: {
      small: { value: '8px' },
      medium: { value: '16px' },
      large: { value: '24px' }
    },
    radii: {
      small: { value: '4px' },
      medium: { value: '8px' },
      large: { value: '12px' }
    }
  }
};

// Custom form fields configuration
const formFields = {
  signIn: {
    username: {
      placeholder: 'Ingrese su usuario',
      label: 'Usuario',
      isRequired: true
    },
    password: {
      placeholder: 'Ingrese su contraseña',
      label: 'Contraseña',
      isRequired: true
    }
  }
};

// Custom components configuration
const components = {
  Header() {
    return (
      <div style={{ 
        textAlign: 'center',
        padding: '32px 0 16px',
        marginBottom: '32px',
        background: '#F5F5F5',
        borderRadius: '12px',
        margin: '24px'
      }}>
        <img 
          src={icon} 
          alt="Almida" 
          style={{
            width: '64px',
            height: '64px',
            marginBottom: '16px'
          }}
        />
        <h1 style={{
          margin: 0,
          color: '#333333',
          fontSize: '28px',
          fontWeight: 600
        }}>
          Almida
        </h1>
        <p style={{
          margin: '8px 0 0',
          color: '#666666',
          fontSize: '16px'
        }}>
          Sistema de Control de Gastos
        </p>
      </div>
    );
  }
};

// User info interface
interface UserInfo {
  id: string;
  name: string;
  email: string;
}

function App({ signOut, user }: WithAuthenticatorProps) {
  const [view, setView] = useState<View>('transactions');
  const [username, setUsername] = useState<string>('');
  const [userId, setUserId] = useState<string>('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  
  // Create a client for API operations
  const client = generateClient({
    authMode: 'userPool'
  });

  useEffect(() => {
    suppressResizeObserverError();
    const initializeUser = async () => {
      try {
        // Get the authenticated Cognito user
        const cognitoUser = await getCurrentUser();
        
        const userInfo: UserInfo = {
          id: cognitoUser.userId,
          name: cognitoUser.username.split('@')[0], // Use email prefix as name
          email: cognitoUser.username
        };
        
        setUsername(userInfo.name);
        setUserId(userInfo.id);
        
        // Check if the user exists in the database
        try {
          const getUserResult = await client.graphql({
            query: getUser,
            variables: { id: userInfo.id }
          }) as GraphQLResult<{ getUser: any }>;
          
          if (!getUserResult.data?.getUser) {
            console.log('User not found in database, creating user record');
            
            // User doesn't exist in database, create it
            const createUserResult = await client.graphql({
              query: createUser,
              variables: {
                input: {
                  id: userInfo.id,
                  email: userInfo.email,
                  name: userInfo.name
                }
              }
            });
            
            console.log('User created successfully:', createUserResult);
          } else {
            console.log('User found in database:', getUserResult.data.getUser);
          }
        } catch (error) {
          console.error('Error checking/creating user in database:', error);
        }
      } catch (error) {
        console.error('Error initializing user:', error);
        // Fallback to username if attributes are not available
        if (user?.username) {
          setUsername(user.username);
        } else {
          setUsername('Unknown User');
        }
        setUserId('unknown');
      } finally {
        setIsLoading(false);
      }
    };

    initializeUser();
  }, [user?.username]);

  const handleTransactionCreated = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Don't render main content until we have user info
  if (isLoading) {
    return <div className="loading">Cargando...</div>;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-brand">
            <img src={icon} alt="Stratto" />
            <h1>Almida</h1>
          </div>
          <div className="user-controls" onClick={signOut}>
            <span className="username">{username}</span>
            <span className="logout-text">Logout</span>
          </div>
        </div>
        <nav className="header-nav">
          <button 
            className={`nav-button ${view === 'transactions' ? 'active' : ''}`}
            onClick={() => setView('transactions')}
          >
            Transacciones
          </button>
          <button 
            className={`nav-button ${view === 'audit' ? 'active' : ''}`}
            onClick={() => setView('audit')}
          >
            Actividad
          </button>
          <button 
            className={`nav-button ${view === 'report' ? 'active' : ''}`}
            onClick={() => setView('report')}
          >
            Reporte
          </button>
        </nav>
      </header>
      <main className="App-main">
        {view === 'transactions' && (
          <>
            <TransactionForm userId={userId} onTransactionCreated={handleTransactionCreated} />
            <TransactionList userId={userId} refreshKey={refreshKey} />
          </>
        )}
        {view === 'audit' && <AuditLog />}
        {view === 'report' && <Report />}
        {view === 'users' && <UserManagement />}
      </main>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <div className="mobile-nav-buttons">
          <button 
            className={`mobile-nav-button ${view === 'users' ? 'active' : ''}`}
            onClick={() => setView('users')}
          >
            ⚙️
            <span>Usuarios</span>
          </button>
          <button 
            className={`mobile-nav-button ${view === 'transactions' ? 'active' : ''}`}
            onClick={() => setView('transactions')}
          >
            💰
            <span>Transacciones</span>
          </button>
          <button 
            className={`mobile-nav-button ${view === 'report' ? 'active' : ''}`}
            onClick={() => setView('report')}
          >
            📊
            <span>Reporte</span>
          </button>
          <button 
            className={`mobile-nav-button ${view === 'audit' ? 'active' : ''}`}
            onClick={() => setView('audit')}
          >
            📝
            <span>Historial</span>
          </button>
        </div>
      </nav>
    </div>
  );
}

// Wrap the app with authentication and theme
const AppWithAuth = withAuthenticator(App, {
  signUpAttributes: [],
  hideSignUp: true,
  components,
  formFields
});

// Export the themed version
export default function ThemedApp() {
  return (
    <ThemeProvider theme={theme}>
      <AppWithAuth />
    </ThemeProvider>
  );
}
